import React, { useState } from "react";
import { Typography } from "@mui/material";
import { Layout } from "../Layouts/Layout";
import { useQuery } from "@apollo/client";
import {
  GET_SITE_METRICS,
  GET_SITE_COURSE_METRICS,
  GET_SITE_INSTRUCTOR_METRICS,
  GET_SITE_STUDENT_METRICS,
  SiteMetricsResult,
  SiteCourseMetricsResult,
  SiteInstructorMetricsResult,
  SiteStudentMetricsResult,
} from "../Queries";
import dayjs from "dayjs";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { DateFilters } from "../Components/SiteDashboard/DateFilters";
import { MetricsCards } from "../Components/SiteDashboard/MetricsCards";
import { CourseList } from "../Components/SiteDashboard/CourseList";
import { InstructorList } from "../Components/SiteDashboard/InstructorList";
import { StudentList } from "../Components/SiteDashboard/StudentList";

type ExpandedPanel = "courses" | "instructors" | "students" | null;

export function SiteDashboard() {
  const { setError } = useErrorBoundary();
  const [expandedPanel, setExpandedPanel] = useState<ExpandedPanel>("courses");

  // Default to 1 year ago through today
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "year"));
  const [endDate, setEndDate] = useState(dayjs());
  const [coursePrefix, setCoursePrefix] = useState("");

  const { loading, data } = useQuery<SiteMetricsResult>(GET_SITE_METRICS, {
    variables: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      coursePrefix: coursePrefix || undefined,
    },
    onError: (error) => setError("Error loading site metrics", error),
  });

  const { loading: courseLoading, data: courseData } =
    useQuery<SiteCourseMetricsResult>(GET_SITE_COURSE_METRICS, {
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        coursePrefix: coursePrefix || undefined,
      },
      onError: (error) => setError("Error loading course metrics", error),
      skip: expandedPanel !== "courses",
    });

  const { loading: instructorLoading, data: instructorData } =
    useQuery<SiteInstructorMetricsResult>(GET_SITE_INSTRUCTOR_METRICS, {
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        coursePrefix: coursePrefix || undefined,
      },
      onError: (error) => setError("Error loading instructor metrics", error),
      skip: expandedPanel !== "instructors",
    });

  const { loading: studentLoading, data: studentData } =
    useQuery<SiteStudentMetricsResult>(GET_SITE_STUDENT_METRICS, {
      variables: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        coursePrefix: coursePrefix || undefined,
      },
      onError: (error) => setError("Error loading student metrics", error),
      skip: expandedPanel !== "students",
    });

  const metrics = data?.siteMetrics;
  const courses = courseData?.siteCourseMetrics || [];
  const instructors = instructorData?.siteInstructorMetrics || [];
  const students = studentData?.siteStudentMetrics || [];

  const handlePanelToggle = (panel: ExpandedPanel) => {
    setExpandedPanel(expandedPanel === panel ? null : panel);
  };

  return (
    <Layout breadcrumbs={[{ title: "Usage Dashboard" }]}>
      <DateFilters
        startDate={startDate}
        endDate={endDate}
        coursePrefix={coursePrefix}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onCoursePrefixChange={setCoursePrefix}
      />

      <MetricsCards loading={loading} metrics={metrics} />

      <CourseList
        isExpanded={expandedPanel === "courses"}
        loading={courseLoading}
        courses={courses}
        onToggleExpand={() => handlePanelToggle("courses")}
      />

      <InstructorList
        isExpanded={expandedPanel === "instructors"}
        loading={instructorLoading}
        instructors={instructors}
        onToggleExpand={() => handlePanelToggle("instructors")}
      />

      <StudentList
        isExpanded={expandedPanel === "students"}
        loading={studentLoading}
        students={students}
        onToggleExpand={() => handlePanelToggle("students")}
      />
    </Layout>
  );
}
